import React, { useState, useEffect, useRef } from 'react';

/* ── Helpers ── */
function addWorkdays(start, n) {
  const d = new Date(start);
  if (n === 0) return d;
  let added = 0;
  while (added < n) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return d;
}

function countWorkdays(from, to) {
  let count = 0;
  const d = new Date(from);
  d.setHours(0, 0, 0, 0);
  const end = new Date(to);
  end.setHours(0, 0, 0, 0);
  while (d < end) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) count++;
  }
  return count;
}

function dateLabel(date) {
  if (!date) return 'Datum wählen';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(date); d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / 86400000);
  if (diff < 0) return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Morgen';
  const wd = countWorkdays(today, d);
  const weekday = d.toLocaleDateString('de-DE', { weekday: 'long' });
  if (diff <= 14) return `In ${wd} Werktag${wd !== 1 ? 'en' : ''} (${weekday})`;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const MONTH_NAMES = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
const PRIORITY_DOT = { Keine: '#93a89a', Niedrig: '#159c5f', Mittel: '#f59e0b', Hoch: '#dc2626' };

/* ── Mini Calendar ── */
function MiniCalendar({ value, onChange }) {
  const today = new Date();
  const [view, setView] = useState(() => {
    const d = value ? new Date(value) : today;
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const { year, month } = view;
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const QUICK = [
    { label: 'Heute',     date: new Date() },
    { label: 'Morgen',    date: addWorkdays(new Date(), 1) },
    { label: '3 Werktage', date: addWorkdays(new Date(), 3) },
    { label: '1 Woche',   date: addWorkdays(new Date(), 7) },
  ];

  return (
    <div style={{
      position: 'absolute', top: '110%', left: 0, zIndex: 600, marginTop: 2,
      background: 'var(--panel-bg)', borderRadius: 12, border: '1px solid var(--border)',
      boxShadow: '0 10px 32px rgba(15,30,60,0.18)', padding: '14px 14px 12px', width: 248,
    }}>
      {/* Month nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <NavBtn onClick={() => setView(v => { const d = new Date(v.year, v.month - 1); return { year: d.getFullYear(), month: d.getMonth() }; })}>‹</NavBtn>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{MONTH_NAMES[month]} {year}</span>
        <NavBtn onClick={() => setView(v => { const d = new Date(v.year, v.month + 1); return { year: d.getFullYear(), month: d.getMonth() }; })}>›</NavBtn>
      </div>

      {/* Weekday headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
        {['Mo','Di','Mi','Do','Fr','Sa','So'].map(d => (
          <div key={d} style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textAlign: 'center', padding: '2px 0' }}>{d}</div>
        ))}
      </div>

      {/* Day cells */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const isToday    = d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
          const isSelected = value && d === new Date(value).getDate() && month === new Date(value).getMonth() && year === new Date(value).getFullYear();
          return (
            <button key={d} onClick={() => onChange(new Date(year, month, d))} style={{
              width: 32, height: 32, borderRadius: 8,
              background: isSelected ? '#159c5f' : 'transparent',
              border: isToday && !isSelected ? '1.5px solid rgba(29,190,116,0.45)' : '1px solid transparent',
              color: isSelected ? '#fff' : isToday ? '#159c5f' : 'var(--text)',
              fontSize: 12, fontWeight: (isSelected || isToday) ? 600 : 400,
              cursor: 'pointer', transition: 'background .1s',
            }}
            onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--bg4)'; }}
            onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
            >{d}</button>
          );
        })}
      </div>

      {/* Quick picks */}
      <div style={{ borderTop: '1px solid var(--border)', marginTop: 10, paddingTop: 10, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {QUICK.map(q => (
          <button key={q.label} onClick={() => onChange(q.date)} style={{
            fontSize: 11, padding: '3px 9px', borderRadius: 20, cursor: 'pointer',
            background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', color: 'var(--accent)',
          }}>{q.label}</button>
        ))}
      </div>
    </div>
  );
}

function NavBtn({ onClick, children }) {
  return (
    <button onClick={onClick} style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg3)', cursor: 'pointer', fontSize: 14, color: 'var(--text2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{children}</button>
  );
}

/* ── FloatingTask ── */
export default function FloatingTask({ lead, currentUser, users = [], onSave, onClose }) {
  const isAdmin = !currentUser?.role || currentUser.role === 'admin';
  const defaultUser = { id: currentUser?.id || null, name: currentUser?.name || 'Robin Obrecht' };
  // All users can assign to others (admin sees everyone, regular user sees admin + themselves)
  const assignableUsers = isAdmin
    ? users
    : users.filter(u => !u.role || u.role === 'admin' || u.id === currentUser?.id);
  const [title,          setTitle]          = useState('');
  const [dueDate,        setDueDate]        = useState(() => addWorkdays(new Date(), 3));
  const [dueTime,        setDueTime]        = useState('09:00');
  const [repeat,         setRepeat]         = useState(false);
  const [taskType,       setTaskType]       = useState('To-do');
  const [priority,       setPriority]       = useState('Keine');
  const [notes,          setNotes]          = useState('');
  const [assignedTo,     setAssignedTo]     = useState(defaultUser.name);
  const [assignedUserId, setAssignedUserId] = useState(defaultUser.id);
  const [showCal,        setShowCal]        = useState(false);
  const [minimized,      setMinimized]      = useState(false);
  const [pos, setPos]                       = useState({ x: Math.max(60, window.innerWidth / 2 - 250), y: Math.max(50, window.innerHeight / 2 - 320) });

  const dragging = useRef(false);
  const dragOff  = useRef({ x: 0, y: 0 });

  useEffect(() => {
    function onMove(e) { if (!dragging.current) return; setPos({ x: e.clientX - dragOff.current.x, y: e.clientY - dragOff.current.y }); }
    function onUp()   { dragging.current = false; }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  useEffect(() => {
    function handleKey(e) { if (e.key === 'Escape') setShowCal(false); }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  function startDrag(e) {
    if (e.target.closest('button, input, select, textarea')) return;
    dragging.current = true;
    dragOff.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  }

  function handleSave() {
    if (!title.trim()) return;
    const dt = new Date(dueDate);
    const [h, m] = dueTime.split(':').map(Number);
    dt.setHours(h, m, 0, 0);
    onSave({
      id: Date.now(),
      title: title.trim(),
      type: taskType,
      priority,
      dueDate: dueDate.toISOString().slice(0, 10),
      dueTime,
      dueDatetime: dt.toISOString(),
      notes,
      assignedTo,
      assignedUserId,
      lead: lead ? { id: lead.id, name: lead.name, company: lead.company } : null,
      done: false,
      dismissed: false,
    });
    onClose();
  }

  const canSave = title.trim().length > 0;

  return (
    <div style={{
      position: 'fixed', left: pos.x, top: pos.y, zIndex: 410,
      width: 500, background: 'var(--panel-bg)',
      borderRadius: 12, border: '1px solid var(--border)',
      boxShadow: '0 16px 56px rgba(15,30,60,0.22)', overflow: 'hidden',
      fontFamily: 'var(--font)',
    }}>

      {/* ── Header ── */}
      <div onMouseDown={startDrag} style={{
        background: '#3d5a7a', padding: '10px 14px',
        cursor: 'grab', userSelect: 'none',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        {/* Drag dots */}
        <svg width="12" height="18" viewBox="0 0 12 18" fill="rgba(255,255,255,0.45)">
          {[0,6,12].map(y => [0,6].map(x => <circle key={`${x}${y}`} cx={x+3} cy={y+3} r={1.5}/>))}
        </svg>
        <button onClick={() => setMinimized(m => !m)} style={hdrBtn}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2">
            <polyline points={minimized ? '6 15 12 9 18 15' : '6 9 12 15 18 9'}/>
          </svg>
        </button>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#fff', flex: 1 }}>Aufgabe</span>
        <button onClick={onClose} style={hdrBtn}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      {!minimized && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>

          {/* Title input */}
          <div style={{ padding: '16px 18px 12px' }}>
            <input
              autoFocus
              value={title}
              onChange={e => setTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && canSave) handleSave(); }}
              placeholder="Aufgabe eingeben…"
              style={{
                width: '100%', height: 48, borderRadius: 8,
                border: '2px solid #159c5f',
                padding: '0 14px', fontSize: 15, outline: 'none',
                background: 'var(--bg2)', color: 'var(--text)',
                fontFamily: 'var(--font)', boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ padding: '0 18px', display: 'flex', flexDirection: 'column', gap: 0 }}>

            {/* Date / Time / Reminder */}
            <div style={{ paddingBottom: 14 }}>
              <div style={secLabel}>Aktivitätsdatum</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 5, flexWrap: 'wrap' }}>
                {/* Date picker */}
                <div style={{ position: 'relative' }}>
                  <button onClick={() => setShowCal(c => !c)} style={blueValue}>
                    {dateLabel(dueDate)}
                  </button>
                  {showCal && (
                    <div onMouseDown={e => e.stopPropagation()}>
                      <MiniCalendar value={dueDate} onChange={d => { setDueDate(d); setShowCal(false); }} />
                    </div>
                  )}
                </div>

                {/* Time */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                  <input
                    type="time" value={dueTime} onChange={e => setDueTime(e.target.value)}
                    style={{
                      border: 'none', outline: 'none', background: 'transparent',
                      fontSize: 14, fontWeight: 600, color: '#0f8a53',
                      fontFamily: 'var(--font)', cursor: 'pointer', width: 80,
                    }}
                  />
                </div>

                {/* Reminder */}
                <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                  <div style={secLabel}>Erinnerung</div>
                  <button style={{ ...blueValue, marginTop: 3 }}>Keine Erinnerung</button>
                </div>
              </div>
            </div>

            <Divider />

            {/* Repeat */}
            <div style={{ padding: '12px 0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <div onClick={() => setRepeat(r => !r)} style={{
                  width: 18, height: 18, borderRadius: 4, border: `2px solid ${repeat ? '#159c5f' : 'var(--border2)'}`,
                  background: repeat ? '#159c5f' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all .12s', flexShrink: 0, cursor: 'pointer',
                }}>
                  {repeat && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
                </div>
                <span style={{ fontSize: 14, color: 'var(--text2)' }}>Auf Wiederholen festlegen</span>
              </label>
            </div>

            <Divider />

            {/* Type / Priority / Queue */}
            <div style={{ padding: '12px 0', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <div style={secLabel}>Aufgabentyp</div>
                <select value={taskType} onChange={e => setTaskType(e.target.value)} style={inlineSelect}>
                  <option>To-do</option>
                  <option>Anruf</option>
                  <option>E-Mail</option>
                  <option>Besuch</option>
                </select>
              </div>
              <div>
                <div style={secLabel}>Priorität</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: PRIORITY_DOT[priority] || '#93a89a', flexShrink: 0 }} />
                  <select value={priority} onChange={e => setPriority(e.target.value)} style={inlineSelect}>
                    <option>Keine</option>
                    <option>Niedrig</option>
                    <option>Mittel</option>
                    <option>Hoch</option>
                  </select>
                </div>
              </div>
              <div>
                <div style={secLabel}>Warteschlange</div>
                <button style={{ ...blueValue, marginTop: 4 }}>Nicht vorhanden</button>
              </div>
            </div>

            {/* Assigned to */}
            <div style={{ paddingBottom: 12 }}>
              <div style={secLabel}>Aktivität zugewiesen an</div>
              {assignableUsers.length > 1 ? (
                <select
                  value={assignedUserId || ''}
                  onChange={e => {
                    const u = assignableUsers.find(x => x.id === e.target.value);
                    setAssignedUserId(e.target.value || null);
                    setAssignedTo(u?.name || u?.email || assignedTo);
                  }}
                  style={{ ...inlineSelect, marginTop: 4 }}
                >
                  {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                </select>
              ) : (
                <span style={{ ...blueValue, marginTop: 4, cursor: 'default', display: 'inline-block' }}>
                  {assignedTo}
                </span>
              )}
            </div>

            <Divider />

            {/* Notes */}
            <div style={{ padding: '12px 0 8px' }}>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Notizen …"
                rows={4}
                style={{
                  width: '100%', border: 'none', outline: 'none', resize: 'none',
                  background: 'transparent', color: 'var(--text)',
                  fontSize: 14, lineHeight: 1.7, fontFamily: 'var(--font)',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Formatting toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, paddingBottom: 10, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
              {[
                { label: 'B', style: { fontWeight: 700 } },
                { label: 'I', style: { fontStyle: 'italic' } },
                { label: 'U', style: { textDecoration: 'underline' } },
                { label: 'T\u0336', style: { textDecoration: 'line-through' } },
              ].map(b => (
                <button key={b.label} style={{ ...toolbarBtn, ...b.style }}>{b.label}</button>
              ))}
              <span style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 4px' }}/>
              <button style={toolbarBtn}>Mehr ▾</button>
              <span style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 4px' }}/>
              {/* link */}
              <button style={toolbarBtn}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg></button>
              {/* image */}
              <button style={toolbarBtn}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></button>
              {/* align */}
              <button style={toolbarBtn}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2"><line x1="21" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="21" y1="18" x2="3" y2="18"/></svg></button>
              <span style={{ flex: 1 }}/>
              {lead && (
                <button style={{ fontSize: 12, color: '#0f8a53', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  1 Datensatz zugeordnet ▾
                </button>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '12px 0 16px', display: 'flex', justifyContent: 'flex-start' }}>
              <button onClick={handleSave} disabled={!canSave} style={{
                height: 38, padding: '0 22px', borderRadius: 8,
                background: canSave ? 'linear-gradient(135deg,#0f8a53,#159c5f)' : 'var(--bg4)',
                border: 'none',
                color: canSave ? '#fff' : 'var(--text3)',
                fontSize: 14, fontWeight: 600,
                cursor: canSave ? 'pointer' : 'default',
                boxShadow: canSave ? '0 2px 10px rgba(29,190,116,0.3)' : 'none',
                transition: 'all .15s',
              }}>Erstellen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── style constants ── */
function Divider() {
  return <div style={{ height: 1, background: 'var(--border)', margin: '0' }} />;
}

const hdrBtn = {
  width: 26, height: 26, borderRadius: 6,
  background: 'rgba(255,255,255,0.1)', border: 'none',
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
};

const secLabel = {
  fontSize: 12, color: 'var(--text3)', fontWeight: 400,
};

const blueValue = {
  background: 'none', border: 'none', cursor: 'pointer',
  fontSize: 14, fontWeight: 600, color: '#0f8a53',
  padding: 0, textAlign: 'left',
};

const inlineSelect = {
  background: 'none', border: 'none', cursor: 'pointer',
  fontSize: 14, fontWeight: 600, color: '#0f8a53',
  padding: 0, outline: 'none', marginTop: 4,
  fontFamily: 'var(--font)',
};

const toolbarBtn = {
  minWidth: 26, height: 26, borderRadius: 4,
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--text3)', fontSize: 12, fontWeight: 600,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px',
};
